<script setup lang="ts">
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Illustration from '@leaflink/stash/Illustration.vue';
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  import i18n from '@/plugins/i18n';

  import {
    DESKTOP_SCREEN_MIN_WIDTH,
    HEADER_MAX_HEIGHT,
    HEADER_MIN_HEIGHT,
    useFeatureEnrollmentProvider,
  } from './FeatureEnrollmentLayout.vue';

  const { steps, activeStepIndex, activeSubstepIndex, isSidenavActive, bodyRef, headerRef, openExitDialog } =
    useFeatureEnrollmentProvider();

  const activeStep = computed(() => steps[activeStepIndex.value]);
  const activeSubstep = computed(() => steps[activeStepIndex.value].substeps?.[activeSubstepIndex.value]);
  const activeIllustration = computed(() => activeSubstep.value?.illustration || activeStep.value.illustration);

  const bodyComponentScrollTop = ref(0);

  // Returns a proportional range value based on HEADER_MAX_HEIGHT and HEADER_MIN_HEIGHT
  const proportionalPxRange = (start: number, end: number) => {
    const max = Math.max(start, end);
    const min = Math.min(start, end);

    const proportionalFactor = (max - min) / (HEADER_MAX_HEIGHT - HEADER_MIN_HEIGHT);

    if (start > end) {
      return Math.max(min, max - bodyComponentScrollTop.value * proportionalFactor) + 'px';
    }

    return Math.min(max, min + bodyComponentScrollTop.value * proportionalFactor) + 'px';
  };

  // 0.0 to 1.0
  const dynamicZeroToOneDecimalRange = computed(() =>
    Math.min(1, bodyComponentScrollTop.value / (HEADER_MAX_HEIGHT - HEADER_MIN_HEIGHT)).toFixed(1),
  );

  const dynamicHeaderHeight = computed(
    () => Math.max(HEADER_MIN_HEIGHT, HEADER_MAX_HEIGHT - bodyComponentScrollTop.value) + 'px',
  );

  const headerPaddingBottomStart = 16;
  const headerPaddingBottomEnd = 10;
  const dynamicHeaderPaddingBottom = computed(() =>
    proportionalPxRange(headerPaddingBottomStart, headerPaddingBottomEnd),
  );

  const actionButtonsPositionTopStart = 36;
  const actionButtonsPositionTopEnd = 16;
  const dynamicActionButtonsPositionTop = computed(() =>
    proportionalPxRange(actionButtonsPositionTopStart, actionButtonsPositionTopEnd),
  );

  const dynamicTitleIconOpacity = computed(() => 1 - parseFloat(dynamicZeroToOneDecimalRange.value) * 1.5);

  const dynamicTitleIconFilter = computed(
    () => `blur(${proportionalPxRange(0, 20)}) grayscale(${parseFloat(dynamicZeroToOneDecimalRange.value) * 3})`,
  );

  const dynamicTitleTranslateX = computed(() => {
    const iconWidth = 60; // icon = step illustration icon
    const titleFlexGap = 12;
    const menuIconWidth = 24;
    const menuIconPadding = 12;

    const gutter = {
      // Mobile-Tablet
      md: {
        withIcon: -(iconWidth + titleFlexGap - menuIconWidth - menuIconPadding),
        noIcon: menuIconWidth + menuIconPadding,
      },

      // Desktop
      lg: {
        withIcon: -(iconWidth + titleFlexGap),
        noIcon: 0, // there will be no translate-x animation
      },
    };

    const isDesktop = window.innerWidth >= DESKTOP_SCREEN_MIN_WIDTH;
    const screenSize = isDesktop ? 'lg' : 'md';
    const iconStatus = activeIllustration.value ? 'withIcon' : 'noIcon';

    return proportionalPxRange(0, gutter[screenSize][iconStatus]);
  });

  const onBodyComponentScroll = () => {
    bodyComponentScrollTop.value = bodyRef.value?.scrollTop || 0;
  };

  onMounted(() => {
    bodyRef.value?.addEventListener('scroll', onBodyComponentScroll);
  });

  onUnmounted(() => {
    bodyRef.value?.removeEventListener('scroll', onBodyComponentScroll);
  });
</script>

<template>
  <header class="tw-flex tw-items-end tw-min-h-[--header-max-height] tw-translate-y-[-100%]" ref="headerRef">
    <div
      class="header-content tw-flex tw-flex-1 tw-items-end tw-px-12 tw-bg-white tw-translate-y-full lg:tw-px-[84px]"
      :style="{
        height: dynamicHeaderHeight,
        paddingBottom: dynamicHeaderPaddingBottom,
      }"
    >
      <div
        class="tw-flex tw-items-center tw-justify-between tw-absolute tw-left-0 tw-w-full tw-z-[1] tw-px-[36px] lg:tw-px-[72px] lg:tw-justify-end"
        :style="{
          top: dynamicActionButtonsPositionTop,
        }"
      >
        <Button class="lg:hidden" icon data-test="button|header-menu" @click="isSidenavActive = true">
          <Icon name="menu" />
        </Button>

        <Button
          class="tw-transition-[.2s] lg:tw-opacity-100"
          :class="{ 'tw-opacity-0': isSidenavActive }"
          icon
          data-test="header|exit-button"
          :title="i18n.t('common.exit')"
          @click="openExitDialog"
        >
          <Icon name="close" />
        </Button>
      </div>

      <div class="tw-flex tw-items-center tw-gap-3 tw-h-[60px]">
        <Illustration
          v-if="activeIllustration"
          :name="activeIllustration"
          :size="60"
          role="img"
          :style="{
            opacity: dynamicTitleIconOpacity,
            filter: dynamicTitleIconFilter,
            transition: '.1s',
          }"
        />
        <h2
          class="tw-text-2xl"
          :style="{
            transform: `translateX(${dynamicTitleTranslateX})`,
          }"
        >
          {{ activeSubstep?.text || activeStep.text }}
        </h2>
      </div>
    </div>
  </header>
</template>

<style scoped>
  .header-content::after {
    display: block;
    position: absolute;
    inset: 0;
    /* stylelint-disable-next-line value-keyword-case -- v-bind exception */
    opacity: v-bind(dynamicZeroToOneDecimalRange);
    content: '';
    z-index: -1;

    @apply tw-shadow-md;
  }
</style>
